import type { Update } from "@tauri-apps/plugin-updater"
import { useCallback, useEffect, useRef, useState } from "react"

import { useRecordingState } from "../../contexts/RecordingStateContext"
import { extractErrorMessage } from "../../lib/api/errors"
import { IS_TAURI } from "../../lib/runtime-origin"
import { UpdateModalView } from "./UpdateModalView"
import type { TUpdateState } from "./UpdateModalView/interfaces"

/**
 * Desktop auto-update gate.
 *
 * Runs only inside the Tauri shell (a no-op in the browser build). On launch it asks the Tauri
 * updater whether a newer signed build exists at our own endpoint and, if so, shows the
 * brand-aware changelog modal. "Install & Relaunch" downloads + swaps the bundle and restarts.
 *
 * Deferred while a recording is active — we never prompt a relaunch mid-meeting (mirrors the
 * PWA UpdatePrompt). The check fires once per launch.
 */
export function DesktopUpdateGate(): React.JSX.Element | null {
  // 1. STATE
  const [update, setUpdate] = useState<Update | null>(null)
  const [state, setState] = useState<TUpdateState>("available")
  const [progress, setProgress] = useState<number>(0)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [dismissed, setDismissed] = useState<boolean>(false)

  // 2. EXTERNAL HOOKS
  const { isRecordingActive } = useRecordingState()

  // refs
  const checkedRef = useRef<boolean>(false)

  // 5. EFFECTS — check once on launch (desktop only, never mid-recording)
  useEffect(() => {
    if (!IS_TAURI || checkedRef.current || isRecordingActive) return
    checkedRef.current = true
    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const { check } = await import("@tauri-apps/plugin-updater")
        const found = await check()
        if (!cancelled && found) setUpdate(found)
      } catch (err) {
        // A failed check must never break the app — log and stay silent.
        console.error("[updater] check failed", err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isRecordingActive])

  // 4. CALLBACKS
  const handleInstall = useCallback((): void => {
    if (!update) return
    setState("downloading")
    setErrorMessage(undefined)
    setProgress(0)
    void (async (): Promise<void> => {
      try {
        let total = 0
        let received = 0
        await update.downloadAndInstall((event) => {
          if (event.event === "Started") {
            total = event.data.contentLength ?? 0
          } else if (event.event === "Progress") {
            received += event.data.chunkLength
            if (total > 0) setProgress(Math.min(100, Math.round((received / total) * 100)))
          } else if (event.event === "Finished") {
            setProgress(100)
          }
        })
        const { relaunch } = await import("@tauri-apps/plugin-process")
        await relaunch()
      } catch (err) {
        console.error("[updater] install failed", err)
        setErrorMessage(extractErrorMessage(err))
        setState("error")
      }
    })()
  }, [update])

  const handleDismiss = useCallback((): void => setDismissed(true), [])

  // 6. GUARD CLAUSES — nothing to show in the browser, with no update, dismissed, or mid-recording
  if (!IS_TAURI || !update || dismissed || isRecordingActive) return null

  // 7. RENDER
  return (
    <UpdateModalView
      opened
      version={update.version}
      notes={update.body ?? ""}
      state={state}
      progress={progress}
      errorMessage={errorMessage}
      onInstall={handleInstall}
      onDismiss={handleDismiss}
    />
  )
}
